import React from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "./Logo";
import { useDemo } from "../context/DemoContext";

const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || "https://app.miseone.eu/login";

// Smoothly scrolls to a section id on the landing page (uses Lenis if available).
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el);
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const LinkItem = ({ children, onClick, href, to, testId, external }) => {
  const base =
    "font-body text-[14px] text-[#6b665e] transition-opacity duration-200 hover:text-[#1b1a18] hover:opacity-100 focus-visible:text-[#1b1a18]";

  if (to) {
    return (
      <Link to={to} data-testid={testId} className={base}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        data-testid={testId}
        className={base}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={`${base} text-left`}>
      {children}
    </button>
  );
};

const Column = ({ label, children }) => (
  <div className="flex flex-col gap-3">
    <div className="font-body text-[11px] font-semibold uppercase tracking-[0.24em] text-[#9a958c]">
      {label}
    </div>
    {children}
  </div>
);

export const Footer = () => {
  const { openDemo } = useDemo();

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-[var(--mo-line)] bg-[#f5f2ea] px-6 pt-16 pb-10 md:px-12 md:pt-20 lg:px-24"
    >
      <div className="mx-auto w-full max-w-[1600px]">
        {/* Brand + tagline */}
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="inline-flex items-center gap-3"
              data-testid="footer-logo"
            >
              <LogoMark size={34} />
              <span className="font-editorial text-2xl tracking-tight text-[#1b1a18]">
                Mise One
              </span>
            </Link>
            <p className="mt-6 font-display text-2xl font-bold leading-[1.1] tracking-tight text-[#1b1a18] sm:text-3xl">
              One Kitchen.
              <br />
              One System.
              <br />
              <span className="text-[#EA6A43]">One Way to Work.</span>
            </p>
          </div>

          {/* Nav columns */}
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7">
            <Column label="Product">
              <LinkItem onClick={() => scrollTo("features")} testId="footer-product">
                Product
              </LinkItem>
              <LinkItem onClick={() => scrollTo("why")} testId="footer-how-it-works">
                How MiseOne Works
              </LinkItem>
              <LinkItem onClick={openDemo} testId="footer-book-demo">
                Book a Demo
              </LinkItem>
              <LinkItem href={LOGIN_URL} testId="footer-login">
                Login
              </LinkItem>
            </Column>

            <Column label="Legal">
              <LinkItem to="/privacy" testId="footer-privacy">
                Privacy Policy
              </LinkItem>
              <LinkItem to="/terms" testId="footer-terms">
                Terms of Service
              </LinkItem>
              <LinkItem to="/master-service-agreement" testId="footer-msa">
                Master Service Agreement
              </LinkItem>
              <LinkItem to="/cookies" testId="footer-cookies">
                Cookie Policy
              </LinkItem>
              <LinkItem to="/dpa" testId="footer-dpa">
                Data Processing Agreement
              </LinkItem>
              <LinkItem to="/sla" testId="footer-sla">
                Service Level Agreement
              </LinkItem>
            </Column>

            <Column label="Company">
              <LinkItem to="/support" testId="footer-support">
                Support
              </LinkItem>
              <LinkItem to="/contact" testId="footer-contact">
                Contact
              </LinkItem>
            </Column>
          </div>
        </div>

        {/* Divider + copyright */}
        <div className="mt-14 flex flex-col items-center gap-3 border-t border-[var(--mo-line)] pt-8 text-center md:flex-row md:justify-between md:text-left">
          <div
            className="font-body text-[11px] uppercase tracking-[0.24em] text-[#9a958c]"
            data-testid="footer-copyright"
          >
            © 2026 MiseOne. All rights reserved.
          </div>
          <div className="font-body text-[13px] italic text-[#9a958c]">
            The Digital Mise en Place.
          </div>
        </div>
      </div>
    </footer>
  );
};
